import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Lock,
  Mail,
  Shield,
  User,
  Building2,
} from "lucide-react";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { authService } from "@/lib/services/authService";
import { GoogleSignInButton } from "@/components/google-signin-button";
import { profileService } from "@/lib/services/profileService";
import { registerSchema, friendlyAuthError } from "@/lib/auth-validation";
import { sendAppEmail } from "@/lib/email/send-app";

import { LAUNCH_LOCKED, isBetaCustomerEmail } from "@/lib/launch-lock";

export const Route = createFileRoute("/register")({
  head: () => ({
    meta: [
      { title: "ClaimGuard | Create an account" },
      { name: "description", content: "Create your customer account to review invoices." },
    ],
  }),
  component: RegisterGate,
});

function RegisterGate() {
  // During launch lock we don't publicly advertise registration, but we still
  // render the form so allowlisted beta customers can create their account.
  // Non-allowlisted emails are rejected inside onSubmit before hitting auth.
  return <RegisterPage />;
}

function RegisterPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    password: "",
    confirm: "",
    company: "",
    terms: false,
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submitInFlight = useRef(false);
  const [checkEmail, setCheckEmail] = useState(false);

  function set<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitInFlight.current) return;
    setError(null);
    setFieldErrors({});
    const parsed = registerSchema.safeParse(form);
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      parsed.error.issues.forEach((i) => (fe[i.path[0] as string] = i.message));
      setFieldErrors(fe);
      return;
    }
    submitInFlight.current = true;
    setSubmitting(true);
    try {
      if (LAUNCH_LOCKED && !isBetaCustomerEmail(parsed.data.email)) {
        setError(
          "ClaimGuard is currently in a private beta. New account registration is paused.",
        );
        return;
      }
      const { data, error: err } = await authService.signUpCustomer({
        email: parsed.data.email,
        password: parsed.data.password,
        fullName: parsed.data.fullName,
      });
      if (err) throw err;

      // Best-effort: patch profile with the optional company name.
      // The DB trigger already created the profile row.
      if (data.user && parsed.data.company) {
        try {
          await profileService.updateMyProfile(data.user.id, {
            full_name: parsed.data.fullName,
          });
        } catch {
          /* non-blocking */
        }
      }

      // If confirmation is disabled, Supabase returns a session and we can log in.
      if (data.session) {
        // Best-effort welcome email — never blocks navigation.
        void sendAppEmail({
          templateName: "welcome",
          recipientEmail: parsed.data.email,
          idempotencyKey: `welcome:${data.user?.id ?? parsed.data.email}`,
          templateData: { customerName: parsed.data.fullName },
        });
        navigate({ to: "/dashboard" });
      } else {
        setCheckEmail(true);
      }
    } catch (err) {
      console.error("[auth] sign-up failed:", err);
      setError(friendlyAuthError(err));
    } finally {
      setSubmitting(false);
      submitInFlight.current = false;
    }
  }

  if (checkEmail) {
    return (
      <div className="flex min-h-dvh flex-col bg-background">
        <SiteHeader />
        <main className="flex-1">
          <div className="container-page grid place-items-center py-14">
            <div className="w-full max-w-md rounded-2xl border bg-card p-8 text-center shadow-sm">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-savings/15 text-savings">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <h1 className="text-xl font-semibold">Check your email</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                We sent a confirmation link to{" "}
                <span className="font-medium text-foreground">{form.email}</span>. Click it to
                activate your account.
              </p>
              <Link
                to="/login"
                className="mt-6 inline-flex items-center justify-center rounded-md bg-navy px-4 py-2 text-sm font-semibold text-navy-foreground hover:opacity-90"
              >
                Back to sign in
              </Link>
            </div>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <SiteHeader />
      <main className="flex-1">
        <div className="container-page grid place-items-center py-14">
          <div className="w-full max-w-lg rounded-2xl border bg-card p-8 shadow-sm">
            <div className="mb-6 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-navy text-navy-foreground">
                <Shield className="h-5 w-5" />
              </div>
              <div>
                <h1 className="text-xl font-semibold tracking-tight">Create your account</h1>
                <p className="text-sm text-muted-foreground">
                  Start reviewing invoices in under a minute.
                </p>
              </div>
            </div>

            {error && (
              <div className="mb-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              <Field
                icon={<User className="h-4 w-4" />}
                label="Full name"
                value={form.fullName}
                onChange={(v) => set("fullName", v)}
                error={fieldErrors.fullName}
              />
              <Field
                icon={<Mail className="h-4 w-4" />}
                label="Email"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(v) => set("email", v)}
                error={fieldErrors.email}
              />
              <Field
                icon={<Building2 className="h-4 w-4" />}
                label="Company (optional)"
                value={form.company}
                onChange={(v) => set("company", v)}
                error={fieldErrors.company}
              />
              <Field
                icon={<Lock className="h-4 w-4" />}
                label="Password"
                type="password"
                autoComplete="new-password"
                value={form.password}
                onChange={(v) => set("password", v)}
                error={fieldErrors.password}
              />
              <Field
                icon={<Lock className="h-4 w-4" />}
                label="Confirm password"
                type="password"
                autoComplete="new-password"
                value={form.confirm}
                onChange={(v) => set("confirm", v)}
                error={fieldErrors.confirm}
              />

              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.terms}
                  onChange={(e) => set("terms", e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-input"
                />
                <span className="text-muted-foreground">
                  I agree to the{" "}
                  <a href="#" className="font-medium text-navy hover:underline">
                    terms of service
                  </a>{" "}
                  and{" "}
                  <a href="#" className="font-medium text-navy hover:underline">
                    privacy policy
                  </a>
                  .
                </span>
              </label>
              {fieldErrors.terms && <p className="text-xs text-destructive">{fieldErrors.terms}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full items-center justify-center rounded-md bg-navy px-4 py-2.5 text-sm font-semibold text-navy-foreground shadow-sm transition hover:opacity-90 disabled:opacity-60"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
              </button>
            </form>

            <div className="mt-5">
              <div className="relative my-3 flex items-center gap-3 text-[11px] uppercase tracking-wider text-muted-foreground">
                <div className="h-px flex-1 bg-border" />
                or
                <div className="h-px flex-1 bg-border" />
              </div>
              <GoogleSignInButton label="Sign up with Google" onError={setError} />
              {LAUNCH_LOCKED && (
                <p className="mt-2 text-center text-xs text-muted-foreground">
                  Private beta: Google sign-up is limited to allowlisted accounts.
                </p>
              )}
            </div>

            <div className="mt-6 border-t pt-4 text-center text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link to="/login" className="font-medium text-navy hover:underline">
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function Field({
  icon,
  label,
  type = "text",
  value,
  onChange,
  error,
  autoComplete,
}: {
  icon: React.ReactNode;
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  autoComplete?: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">{label}</label>
      <div
        className={`flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm focus-within:ring-2 focus-within:ring-navy/20 ${
          error ? "border-destructive" : "border-input"
        }`}
      >
        <span className="text-muted-foreground">{icon}</span>
        <input
          type={type}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent outline-none"
        />
      </div>
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
    </div>
  );
}
